import { faEdit, faTrashAlt, faPlus, faCogs, faCopy } from '@fortawesome/free-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { TFunction } from 'i18next'
import React, { FC, useContext, useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import AppContext from '../gateway/Authentication/AppContext'
import Authorization from '../gateway/Authentication/Authorization'
import AuthService from '../gateway/Authentication/AuthService'
import DialogContext from '../gateway/Authentication/DialogContext'
import { ConfirmDialogProps } from '../gateway/components/ConfirmDialog'
import DataTable from '../gateway/components/DataTable'
import { EditDialogProps } from '../gateway/components/EditDialog'
import ProtectedButton from '../gateway/components/ProtectedButton'
import Loading from '../gateway/layout/Loading'
import TestdataSoapService from './testdata/soap/TestdataSoapService'
import TokenProfileService from '../gateway/service/TokenProfileService'
import { expandRowFunction } from '../gateway/utils/DatatableUtil'
import { FormDataControl } from '../gateway/components/FormFields'

const Policies: FC<{ showGenerateCsvButton?: boolean; header: string; searchPlaceholder: string; helpLink?: string }> = ({
  showGenerateCsvButton = false,
  header,
  searchPlaceholder,
  helpLink,
}) => {
  const { t } = useTranslation()
  const [webServicePolicies, setWebServicePolicies] = useState([] as any[])
  const [tokenProfiles, setTokenProfiles] = useState([] as any[])
  const { userPermissions } = useContext(AppContext)
  const { confirmDialogPropsSetter, editDialogPropsSetter } = useContext(DialogContext)
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    async function loadData() {
      await TestdataSoapService.getAllWebServicepolicies(setWebServicePolicies)
      await TokenProfileService.getConfigurableTokenProfiles(setTokenProfiles)
    }
    loadData().finally(() => setLoading(false))
  }, [])

  const columns = useMemo(() => {
    return [
      {
        text: t('testdata.soap.name'),
        dataField: 'configName',
        sort: true,
        classes: 'position-relative',
      },
      {
        hidden: !AuthService.hasPermission(userPermissions, Authorization.adapter.view),
        dataField: 'df1',
        isDummyField: true,
        text: t('action'),
        headerAlign: 'center',
        formatter: (cellData: any, row: any) => {
          return (
            <>
              <ProtectedButton
                permissions={Authorization.adapter.edit}
                variant='link'
                className='text-decoration-none pr-2'
                tooltip={t('edit')}
                onClick={() => editPolicyDialog(t, editDialogPropsSetter, setWebServicePolicies, row)}>
                <FontAwesomeIcon icon={faEdit} />
              </ProtectedButton>
              <ProtectedButton
                permissions={Authorization.adapter.view}
                variant='link'
                className='text-decoration-none pr-2'
                tooltip={t('testdata.soap.generateCSV')}
                onClick={() => generateCSV(t, editDialogPropsSetter, row)}
                hidden={!showGenerateCsvButton}>
                <FontAwesomeIcon icon={faCogs} />
              </ProtectedButton>
              <ProtectedButton
                permissions={Authorization.adapter.edit}
                variant='link'
                className='text-decoration-none pr-2'
                tooltip={t('testdata.tokenizerConfig.addColumn')}
                onClick={() => addColumnDialog(t, editDialogPropsSetter, setWebServicePolicies, tokenProfiles, row)}>
                <FontAwesomeIcon icon={faPlus} />
              </ProtectedButton>
              <ProtectedButton
                permissions={Authorization.adapter.edit}
                variant='link'
                className='text-decoration-none pr-2'
                tooltip={t('copy')}
                onClick={() => copyPolicyDialog(t, editDialogPropsSetter, setWebServicePolicies, row)}>
                <FontAwesomeIcon icon={faCopy} />
              </ProtectedButton>
              <ProtectedButton
                permissions={Authorization.adapter.edit}
                variant='link'
                className='text-danger text-link text-decoration-none'
                tooltip={t('delete')}
                onClick={() => deletePolicyDialog(t, confirmDialogPropsSetter, setWebServicePolicies, row)}>
                <FontAwesomeIcon icon={faTrashAlt} />
              </ProtectedButton>
            </>
          )
        },
      },
    ]
  }, [t, userPermissions, confirmDialogPropsSetter, editDialogPropsSetter, tokenProfiles, showGenerateCsvButton])

  const expandRow = expandRowFunction(renderer, () => {}, t, confirmDialogPropsSetter, editDialogPropsSetter, setWebServicePolicies, tokenProfiles)

  const defaultSorted = [
    {
      dataField: 'configName',
      order: 'asc',
    },
  ]

  return (
    <Loading isLoading={loading}>
      <DataTable
        data={webServicePolicies}
        columns={columns}
        caption={header}
        helpLink={helpLink}
        expandRow={expandRow}
        captionButtons={[
          {
            onClick: () => createPolicyDialog(t, editDialogPropsSetter, setWebServicePolicies),
            text: t('new'),
            permissions: Authorization.adapter.edit,
          },
        ]}
        searchPlaceholder={searchPlaceholder}
        defaultSorted={defaultSorted}
      />
    </Loading>
  )
}

function createPolicyDialog(t: TFunction, editDialogPropsSetter: any, setWebServicePolicies: React.Dispatch<any>) {
  const editDialogProps: EditDialogProps = {
    show: true,
    header: t('testdata.soap.createHeader'),
    onSubmit: TestdataSoapService.createPolicy,
    onSubmitExtraArgs: [t, setWebServicePolicies],
    data: [
      {
        id: 'configName',
        type: 'text',
        label: t('testdata.soap.name'),
        placeholder: t('testdata.soap.name'),
        requiredMessage: t('testdata.soap.invalidMessage'),
        required: true,
      },
    ],
    labelGridSize: 3,
  }

  editDialogPropsSetter(editDialogProps)
}

function deletePolicyDialog(t: TFunction, confirmDialogPropsSetter: any, setWebServicePolicies: React.Dispatch<any>, row: any) {
  const deleteDialogProps: ConfirmDialogProps = {
    show: true,
    header: t('testdata.soap.deleteHeader'),
    body: t('testdata.soap.deleteBody', { name: row.configName }),
    submitText: t('yes'),
    onSubmit: TestdataSoapService.deletePolicy,
    onSubmitExtraArgs: [row, t, setWebServicePolicies],
    cancelText: t('no'),
  }
  confirmDialogPropsSetter(deleteDialogProps)
}

function editPolicyDialog(t: TFunction, editDialogPropsSetter: any, setWebServicePolicies: React.Dispatch<any>, row: any) {
  const editDialogProps: EditDialogProps = {
    show: true,
    header: t('testdata.soap.editHeader'),
    onSubmit: TestdataSoapService.updatePolicy,
    onSubmitExtraArgs: [t, setWebServicePolicies],
    data: [
      {
        id: 'id',
        value: row.id,
        type: 'hidden',
        render: false,
      },
      {
        id: 'configName',
        value: row.configName,
        type: 'text',
        label: t('testdata.soap.name'),
        placeholder: t('testdata.soap.name'),
        requiredMessage: t('testdata.soap.invalidMessage'),
        required: true,
      },
    ],
    labelGridSize: 2,
  }
  editDialogPropsSetter(editDialogProps)
}

function copyPolicyDialog(t: TFunction, editDialogPropsSetter: any, setWebServicePolicies: React.Dispatch<any>, row: any) {
  const editDialogProps: EditDialogProps = {
    show: true,
    header: t('testdata.soap.copyHeader'),
    onSubmit: TestdataSoapService.copyPolicy,
    onSubmitExtraArgs: [t, setWebServicePolicies],
    data: [
      {
        id: 'id',
        value: row.id,
        type: 'hidden',
        render: false,
      },
      {
        id: 'configName',
        type: 'text',
        label: t('testdata.soap.name'),
        placeholder: t('testdata.soap.name'),
        requiredMessage: t('testdata.soap.invalidMessage'),
        required: true,
      },
    ],
    labelGridSize: 3,
  }
  editDialogPropsSetter(editDialogProps)
}

function generateCSV(t: TFunction, editDialogPropsSetter: any, row: any) {
  const editDialogProps: EditDialogProps = {
    show: true,
    header: t('testdata.soap.generateCSV'),
    onSubmit: TestdataSoapService.generateCSV,
    data: [
      {
        id: 'id',
        value: row.id,
        type: 'hidden',
        render: false,
      },
      {
        id: 'totalLines',
        type: 'number',
        label: t('testdata.soap.totalLines'),
        placeholder: t('testdata.soap.totalLines'),
        requiredMessage: t('invalidMessage', { label: t('testdata.soap.totalLines') }),
        required: true,
      },
    ],
    labelGridSize: 2,
  }
  editDialogPropsSetter(editDialogProps)
}

function addColumnDialog(t: TFunction, editDialogPropsSetter: any, setWebServicePolicies: React.Dispatch<any>, tokenProfiles: any[], row: any) {
  const encryptionMethods = [
    t('testdata.tokenizerConfig.methods.retainValues'),
    t('testdata.tokenizerConfig.methods.encryption'),
    t('testdata.tokenizerConfig.methods.tokenization'),
    t('testdata.tokenizerConfig.methods.staticReplacement'),
  ]
  const editDialogProps: EditDialogProps = {
    show: true,
    header: t('testdata.tokenizerConfig.addColumn'),
    onSubmit: TestdataSoapService.addTokenizerConfig,
    onSubmitExtraArgs: [t, setWebServicePolicies],
    data: (custom: string[]) => [
      {
        id: 'configId',
        type: 'hidden',
        value: row.id,
      },
      {
        id: 'method',
        type: 'select',
        options: encryptionMethods,
        onChange: (e: any, setCustom: React.Dispatch<any>) => setCustom([e.target.value]),
        value: custom[0],
        label: t('testdata.tokenizerConfig.method'),
        required: true,
      },
      {
        id: 'columnName',
        type: 'text',
        label: t('testdata.tokenizerConfig.name'),
        required: true,
      },

      ...getFinalField(tokenProfiles, custom[0], t),
    ],
    initCustom: [t('testdata.tokenizerConfig.methods.retainValues')],
    labelGridSize: 4,
  }

  editDialogPropsSetter(editDialogProps)
}

function getFinalField(tokenProfiles: any[], custom: string, t: TFunction): FormDataControl[] {
  if (custom === t('testdata.tokenizerConfig.methods.staticReplacement')) {
    return [
      {
        id: 'property',
        type: 'text',
        label: t('testdata.tokenizerConfig.replacementText'),
        required: true,
      },
    ]
  } else if (custom === t('testdata.tokenizerConfig.methods.tokenization')) {
    return [
      {
        id: 'tokenProfile',
        type: 'select',
        options: tokenProfiles.map((profile) => profile.name),
        value: tokenProfiles[0].name,
        label: t('testdata.tokenizerConfig.tokenProfile'),
        required: true,
      },
    ]
  }
  return []
}

function renderer(row: any, rowIndex: any, ...args: any[]) {
  const data: any[] = row.configs
  const t: TFunction = args[0]
  const confirmDialogPropsSetter = args[1]
  const editDialogPropsSetter = args[2]
  const setWebServicePolicies = args[3]
  const tokenProfiles = args[4]

  const columns: any[] = [
    {
      text: t('testdata.tokenizerConfig.name'),
      dataField: 'columnName',
      sort: true,
    },
    {
      text: t('testdata.tokenizerConfig.mode'),
      dataField: 'method',
      formatter: (cell: any, row: any) => {
        if (cell === t('testdata.tokenizerConfig.methods.staticReplacement')) {
          return `${cell} (${row.property})`
        } else if (cell === t('testdata.tokenizerConfig.methods.tokenization')) {
          return `${cell} (${row.tokenProfile})`
        } else {
          return cell
        }
      },
    },
    {
      dataField: 'df1',
      isDummyField: true,
      text: t('action'),
      headerAlign: 'center',
      formatter: (cellData: any, row: any) => {
        return (
          <>
            <ProtectedButton
              permissions={Authorization.adapter.edit}
              variant='link'
              className='text-link text-decoration-none'
              tooltip={t('edit')}
              onClick={() => editColumnDialog(t, editDialogPropsSetter, setWebServicePolicies, tokenProfiles, row)}>
              <FontAwesomeIcon icon={faEdit} />
            </ProtectedButton>
            <ProtectedButton
              permissions={Authorization.adapter.edit}
              variant='link'
              className='text-danger text-link text-decoration-none'
              tooltip={t('delete')}
              onClick={() => deleteColumnDialog(t, confirmDialogPropsSetter, setWebServicePolicies, row)}>
              <FontAwesomeIcon icon={faTrashAlt} />
            </ProtectedButton>
          </>
        )
      },
    },
  ]
  if (!data.length) {
    return <React.Fragment key={row.id} />
  }
  return (
    <React.Fragment key={row.id}>
      <DataTable
        data={data}
        columns={columns}
        searchable={false}
        striped={false}
        hover={false}
        condensed={true}
        noDataIndication={() => <span className='text-center'>{t('testdata.tokenizerConfig.noConfigFound')}</span>}
      />
    </React.Fragment>
  )
}

function deleteColumnDialog(t: TFunction, confirmDialogPropsSetter: any, setWebServicePolicies: React.Dispatch<any>, row: any) {
  const deleteDialogProps: ConfirmDialogProps = {
    show: true,
    header: t('testdata.tokenizerConfig.deleteHeader'),
    body: t('testdata.tokenizerConfig.deleteBody', { name: row.columnName }),
    submitText: t('yes'),
    onSubmit: TestdataSoapService.deleteTokenizerConfig,
    onSubmitExtraArgs: [row, t, setWebServicePolicies],
    cancelText: t('no'),
  }
  confirmDialogPropsSetter(deleteDialogProps)
}

function editColumnDialog(t: TFunction, editDialogPropsSetter: any, setWebServicePolicies: React.Dispatch<any>, tokenProfiles: any[], row: any) {
  const encryptionMethods = [
    t('testdata.tokenizerConfig.methods.retainValues'),
    t('testdata.tokenizerConfig.methods.encryption'),
    t('testdata.tokenizerConfig.methods.tokenization'),
    t('testdata.tokenizerConfig.methods.staticReplacement'),
  ]
  const editDialogProps: EditDialogProps = {
    show: true,
    header: t('testdata.tokenizerConfig.editHeader'),
    onSubmit: TestdataSoapService.updateTokenizerConfig,
    onSubmitExtraArgs: [t, setWebServicePolicies],
    data: (custom: string[]) => [
      {
        id: 'id',
        type: 'hidden',
        value: row.id,
      },
      {
        id: 'configId',
        type: 'hidden',
        value: row.configId,
      },
      {
        id: 'method',
        type: 'select',
        options: encryptionMethods,
        onChange: (e: any, setCustom: React.Dispatch<any>) => setCustom([e.target.value]),
        value: custom[0],
        label: t('testdata.tokenizerConfig.method'),
        required: true,
      },
      {
        id: 'columnName',
        type: 'text',
        value: row.columnName,
        label: t('testdata.tokenizerConfig.name'),
        required: true,
      },
      ...getEditFinalField(row, tokenProfiles, custom[0], t),
    ],
    initCustom: [row.method],
    labelGridSize: 4,
  }

  editDialogPropsSetter(editDialogProps)
}

function getEditFinalField(row: any, tokenProfiles: any[], custom: string, t: TFunction): FormDataControl[] {
  if (custom === t('testdata.tokenizerConfig.methods.staticReplacement')) {
    return [
      {
        id: 'property',
        type: 'text',
        value: row.property,
        label: t('testdata.tokenizerConfig.replacementText'),
        required: true,
      },
    ]
  } else if (custom === t('testdata.tokenizerConfig.methods.tokenization')) {
    return [
      {
        id: 'tokenProfile',
        type: 'select',
        options: tokenProfiles.map((profile) => profile.name),
        value: row.tokenProfile,
        label: t('testdata.tokenizerConfig.tokenProfile'),
        required: true,
      },
    ]
  }
  return []
}
export default Policies
